import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import { environment } from '../../env/environment';

import styles from './styles.module.scss';

import GoogleLogin from '../LoginButtons/GoogleLogin';
import FacebookLogin from '../LoginButtons/FacebookLogin';
import PhoneLogin from '../LoginButtons/PhoneLogin';
import LocalLogin from '../LoginButtons/LocalLogin';
import QRLogin from '../LoginButtons/QRLogin';
import AppleLogin from '../LoginButtons/AppleLogin';
import { isMobile } from '../../helpers/helper';
import LoginWithApp from '../LoginButtons/LoginWithApp';
import reportAmplitudeEvent from '@helpers/analytics';
import { AmplitudeEvents } from '@anghami/neoanalytics/src/enums/events';
import { gateway } from '@helpers/neogateway';
import { ParsedUrlQuery } from 'querystring';
import { IAuthenticateResponse } from '@anghami/neogateway/dist/endpoints/authentication';
import getTelcos, { IGetTelcosResponse } from '@anghami/neogateway/dist/endpoints/getTelcos';

export const Login = ({
	onAuth,
	params,
	setHideModal,
	hideAppLogin,
	hidePhoneLogin
}: {
	onAuth: (authResponse: IAuthenticateResponse) => void;
	params: ParsedUrlQuery;
	setHideModal: (state: boolean) => void;
	hideAppLogin?: boolean;
	hidePhoneLogin?: boolean;
}) => {
	const [hideSubModal, setHideSubModal] = useState(true);
	const [telcos, setTelcos] = useState<IGetTelcosResponse>(null);
	const { t } = useTranslation();

	useEffect(() => {
		gateway.loadSodium();

		gateway
		.callEndpoint(getTelcos, params)
		.then(({ data }) => {
			setTelcos(data);
		})
		.catch(() => {
			setTelcos(null);
		});

		reportAmplitudeEvent(AmplitudeEvents.viewLoginScreen, {});
	}, []);

	return (
		<>
			{hideSubModal ? (
				<>
					{!hideAppLogin && (
						<div className={styles.loginwithapp}>
							<LoginWithApp />
							<div className={styles.divider} />
							<p className={styles.subtitle}>Or</p>
						</div>
					)}
					<GoogleLogin onSuccess={onAuth} onError={console.log} />
					<FacebookLogin onSuccess={onAuth} onError={console.log} />
					{!hidePhoneLogin && telcos?.telcos && telcos?.telcos?.length !== 0 && (
						// hide phone login if no telcos are available
						<PhoneLogin
							telcos={telcos}
							onModalChange={setHideModal}
							onSuccess={onAuth}
							onError={console.log}
						/>
					)}
					<p
						id="popup_other_login_options"
						className={styles.other_login_link}
						onClick={() => {
							setHideSubModal(false);
							reportAmplitudeEvent(AmplitudeEvents.moreLoginOptions, {});
						}}
					>
						{t('Other login options')}
					</p>
				</>
			) : (
				<>
					<AppleLogin onSuccess={onAuth} onError={console.log} />
					<div className={styles.divider} />
					<p className={styles.or_continue_with_email}>{t('or continue by email')}</p>
					<LocalLogin onModalChange={setHideModal} onSuccess={onAuth} onError={console.log} />
					<p
						className={styles['back-btn']}
						onClick={() => {
							setHideSubModal(true);
						}}
					>
						{t('back')}
					</p>
				</>
			)}

			<div className={styles.divider} />

			{!isMobile() && (
				<QRLogin onModalChange={setHideModal} onSuccess={onAuth} onError={console.log} />
			)}

			<div className={styles.divider} />

			<p className={styles['recaptcha-notice']}>
				This site is protected by reCAPTCHA and the Google{' '}
				<a target="_blank" rel="noreferrer" href="https://policies.google.com/privacy">
					Privacy Policy
				</a>{' '}
				and{' '}
				<a target="_blank" rel="noreferrer" href="https://policies.google.com/terms">
					Terms of Service
				</a>{' '}
				apply.
			</p>
		</>
	);
};

export const LoginPopup = ({ params, onClosePopUp = () => null, onAuth }) => {
	const [hideModal, setHideModal] = useState(false);
	const { i18n } = useTranslation();
	const lang = i18n.language;

	return (
		<>
			<div
				id="login_popup"
				className={`${styles.LoginPopup} ${hideModal && styles.hidden} animate-fadeIn`}
			>
				<img
					className={styles.logo}
					alt="Anghami"
					src={`${environment.web_cdn_url}web/assets/img/${
						lang === 'ar'
							? 'logos/NEW_LOGO_HORIZONTAL_AR.png'
							: 'new-anghami-logos/header-logo-fullblack.png'
					}`}
				/>
				<Login params={params} setHideModal={setHideModal} onAuth={onAuth} />
			</div>

			<div className={styles.overlay} onClick={() => onClosePopUp()} />
		</>
	);
};

LoginPopup.propTypes = {
	params: propTypes.object,
	onClosePopUp: propTypes.func,
	onAuth: propTypes.func.isRequired
};

export default LoginPopup;
